'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useEffect, useMemo, useRef, useState } from 'react';
import { 
  Award, Globe, Camera, Users, Landmark, Clock, Shield, Zap, 
  TrendingUp, Sparkles, ArrowDown, ArrowRight 
} from 'lucide-react';

export interface StoneInvestmentTheFirmSectionProps {
  messages?: Record<string, unknown>;
  locale?: string;
  /** URLs of current property images – one is chosen at random for the editorial banner */
  propertyImages?: string[];
}

// --- ANIMATION HOOK ---
const useScrollReveal = (threshold = 0.1) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('opacity-0', 'translate-y-8');
            entry.target.classList.add('opacity-100', 'translate-y-0');
          }
        });
      },
      { threshold, rootMargin: "0px 0px -50px 0px" }
    );
    
    const el = ref.current;
    if (el) {
      const children = el.querySelectorAll('.reveal-child');
      if (children.length > 0) children.forEach(child => observer.observe(child));
      else observer.observe(el);
    }
    return () => observer.disconnect();
  }, [threshold]);
  return ref;
};

// --- TYPES ---
interface PhilosophyItem {
  title: string;
  subtitle: string;
  description: string;
  image: string;
}

interface TenetItemData {
  title: string;
  desc: string;
}

// --- DEFAULT DATA (English fallbacks) ---
const DEFAULT_PHILOSOPHY_ITEMS: PhilosophyItem[] = [
  {
    title: "Less is more",
    subtitle: "Philosophy",
    description: "Mies van der Rohe's maxim is one we put into practice each and every day. We believe in a curated portfolio where every property receives our undivided attention. In a world of noise, we offer clarity.",
    image: "https://media.routeroyal.com/aqISh-FhNsEX3_KTIL72K1YtgeHt0tNulh7WK6isUPI/raw:1/czM6Ly9jb2xsYXBzaWJsZS1wdXJzZS1kbW9rLWRubDd4cy9yb3V0ZXJveWFsL3JveWFsZS9zdGF0aWMvMzRmMDQ2MDhjMzIyMGM2YTk2N2E5NDI4Y2E4NThkZTNiYzIyYmMwZmI2ZTZkODdkMTVjOWY0ZjFiZDEyMDkzZS5wbmc.png"
  },
  {
    title: "Sublimating The Asset",
    subtitle: "The Presentation",
    description: "Your property deserves a sales presentation that reflects its qualities. Each marketing campaign is designed to bring out charm and exceptional features, creating an effective, historical, and architectural showcase.",
    image: "https://media.routeroyal.com/hy4PrVjKXq9uOMjLoMnhgwoM7G-Gqdz5hnyzSEy022k/raw:1/czM6Ly9jb2xsYXBzaWJsZS1wdXJzZS1kbW9rLWRubDd4cy9yb3V0ZXJveWFsL3JveWFsZS9zdGF0aWMvOGMzNDQ1MmMyNjFlNDA5YzIwMzY5YzUyNTQ3OTEwYzQ4NTJkZjE3YmRlZjZmYjIxZmNiZDYxMDdiZGQ0MmE4My5wbmc.png"
  },
  {
    title: "Tailored Assistance",
    subtitle: "The Service",
    description: "Analyzing, selecting, understanding, and admiring. Presenting the right property to the right person. Assisting you at each step in the transaction process. Our service is based on knowing our clients intimately.",
    image: "https://media.routeroyal.com/sKvRIRvCLFnKM3hNjyRRLAch6ZnZizkOuacMzcoHSFc/raw:1/czM6Ly9jb2xsYXBzaWJsZS1wdXJzZS1kbW9rLWRubDd4cy9yb3V0ZXJveWFsL3JveWFsZS9zdGF0aWMvNTA2YTJiMzU5NGE1Njg3ZjNkZTBhNzZjMTE2YjdiMzc5MzFiNTQxYjY5NzNhNWFkNDc0ZDEzNzc4MmY2ZTk4ZC5wbmc.png"
  },
  {
    title: "Culture Architecture",
    subtitle: "The Expertise",
    description: "Supported by input from art historians, our architectural expertise traverses the movements. From Classical to Haussmanian, Art Déco to Modern, we understand the language of your walls.",
    image: "https://media.routeroyal.com/gMGwdTDyasadu8YiIEbACDSWtcuL9B3p_nhHRAlvCNg/raw:1/czM6Ly9jb2xsYXBzaWJsZS1wdXJzZS1kbW9rLWRubDd4cy9yb3V0ZXJveWFsL3JveWFsZS9zdGF0aWMvZTU3NTlkZTY1NjJmZWQyMzA1NzU1NGQ0ZjA2Yjc5YjBlNDkyYmY1Nzg4MDFmZmQxZjVlYTVjYzJhOGY5Mzg2NC5wbmc.png"
  }
];

const TENET_ICONS = [Award, TrendingUp, Shield, Users, Sparkles, Clock, Zap, Globe, Landmark, Camera] as const;

// --- COMPONENTS ---
const EDITORIAL_FALLBACK_IMAGE =
  "https://media.routeroyal.com/aqISh-FhNsEX3_KTIL72K1YtgeHt0tNulh7WK6isUPI/raw:1/czM6Ly9jb2xsYXBzaWJsZS1wdXJzZS1kbW9rLWRubDd4cy9yb3V0ZXJveWFsL3JveWFsZS9zdGF0aWMvMzRmMDQ2MDhjMzIyMGM2YTk2N2E5NDI4Y2E4NThkZTNiYzIyYmMwZmI2ZTZkODdkMTVjOWY0ZjFiZDEyMDkzZS5wbmc.png";

const EditorialImageSection = ({ caption, propertyImages }: { caption?: string; propertyImages?: string[] }) => {
  const revealRef = useScrollReveal();

  const imageSrc = useMemo(() => {
    if (propertyImages && propertyImages.length > 0) {
      return propertyImages[Math.floor(Math.random() * propertyImages.length)] ?? EDITORIAL_FALLBACK_IMAGE;
    }
    return EDITORIAL_FALLBACK_IMAGE;
  }, [propertyImages]);

  return (
    <div ref={revealRef} className="relative w-full aspect-[21/9] overflow-hidden opacity-0 translate-y-8 transition-all duration-1000 ease-out">
      <Image 
        src={imageSrc}
        alt="Stone Investment - Luxury real estate"
        fill
        sizes="100vw"
        className="object-cover grayscale opacity-90 hover:opacity-100 hover:grayscale-0 transition-all duration-1000"
      />
      <div className="absolute inset-0 bg-stone-900/30" />
      <div className="absolute inset-0 flex items-center justify-center">
        <p className="font-serif text-4xl md:text-6xl lg:text-7xl text-white italic text-center leading-tight">
          {caption || 'When less is more'}
        </p>
      </div>
    </div>
  );
};

const TenetItem = ({ 
  icon: Icon, 
  title, 
  desc, 
  index 
}: { 
  icon: React.ElementType; 
  title: string; 
  desc: string; 
  index: number;
}) => (
  <div 
    className="reveal-child group p-8 border border-stone-800/50 hover:bg-stone-50 transition-colors duration-500 hover:border-stone-50 opacity-0 translate-y-8 ease-out" 
    style={{ transitionDelay: `${index * 50}ms` }}
  >
    <Icon size={24} className="text-stone-300 mb-6 group-hover:text-stone-900 transition-colors duration-500" strokeWidth={1} aria-hidden="true" />
    <p className="font-serif text-xl text-stone-50 mb-2 group-hover:text-stone-900 transition-colors duration-500 font-semibold">{title}</p>
    <p className="font-sans text-sm font-light text-stone-300 group-hover:text-stone-600 leading-relaxed transition-colors duration-500">
      {desc}
    </p>
  </div>
);

/**
 * Stone Investment - The Firm Section
 * 
 * Concept One Design (Exact Match):
 * - Hero with "Est. 2008" and "The Firm"
 * - Founders section with image and quote
 * - Philosophy section with sticky image
 * - Tenets grid with hover effects
 * - CTA section with decorative circles
 */
export function StoneInvestmentTheFirmSection({ 
  messages,
  locale = 'en',
  propertyImages,
}: StoneInvestmentTheFirmSectionProps) {
  const [activePhilosophy, setActivePhilosophy] = useState(0);
  const tenetRef = useScrollReveal(0.1);

  const stoneMessages = (messages?.stone || {}) as Record<string, unknown>;
  const firm = (stoneMessages.firm || {}) as Record<string, unknown>;
  const hero = (firm.hero || {}) as Record<string, string>;
  const philosophy = (firm.philosophy || {}) as Record<string, unknown>;
  const tenets = (firm.tenets || {}) as Record<string, unknown>;
  const cta = (firm.cta || {}) as Record<string, string>;

  const philosophyItems: PhilosophyItem[] = useMemo(() => {
    const items = philosophy.items as Array<Record<string, string>> | undefined;
    if (items && items.length > 0) {
      return items.map((item, idx): PhilosophyItem => ({
        title: item.title || DEFAULT_PHILOSOPHY_ITEMS[idx]?.title || '',
        subtitle: item.subtitle || DEFAULT_PHILOSOPHY_ITEMS[idx]?.subtitle || '',
        description: item.description || DEFAULT_PHILOSOPHY_ITEMS[idx]?.description || '',
        image: DEFAULT_PHILOSOPHY_ITEMS[idx]?.image || DEFAULT_PHILOSOPHY_ITEMS[0]?.image || '',
      }));
    }
    return DEFAULT_PHILOSOPHY_ITEMS;
  }, [philosophy.items]);

  const tenetItems: TenetItemData[] = useMemo(() => {
    const items = tenets.items as Array<TenetItemData> | undefined;
    if (items && items.length > 0) return items;
    return [
      { title: "Experience", desc: "15+ years of service with a wise eye and recognized expertise." },
      { title: "Marketing", desc: "Digital, national & international press, and qualified client lists." },
      { title: "Rigor", desc: "Evaluations verified by sales. We sell at the price we estimate." },
      { title: "Network", desc: "Lawyers, architects, wealth managers, and notaries at your disposal." },
      { title: "Sublimation", desc: "Home-staging advice and professional photo/video reporting." },
      { title: "Speed", desc: "Sales deadlines of less than 6 months on average." },
      { title: "Ethics", desc: "'Less is more'. Fewer estates for maximum efficiency." },
      { title: "Global", desc: "Exclusive partnership with VIP Real Estate in London." },
      { title: "Culture", desc: "Original approach supported by art historians." },
      { title: "Results", desc: "Proven track record in mansions, luxury apartments, and wine estates." },
    ];
  }, [tenets.items]);

  return (
    <div className="w-full bg-stone-50 min-h-screen pb-24">
      
      {/* --- ACT 1: HERO --- */}
      <header className="relative w-full h-dvh flex flex-col items-center justify-center bg-stone-900 text-stone-50 overflow-hidden">
        {/* Background Texture */}
        <div className="absolute inset-0 opacity-40 animate-pulse" style={{ animationDuration: '10s' }}>
          <Image 
            src="https://media.routeroyal.com/fjETEVoUOPWu5le6XdTQ_ZW9aIJjCqv6nIc10_zieKs/raw:1/czM6Ly9jb2xsYXBzaWJsZS1wdXJzZS1kbW9rLWRubDd4cy9yb3V0ZXJveWFsL3JveWFsZS9zdGF0aWMvMzQ5OTRjMDdiODM0OTVhNTRmODE2NjcwYWIzMmRjNDA1NDMyZGVlYTY1NjA3ZWVhZjRlMjU4ZTYxN2ZkOTkyMi5wbmc.png"
            alt="Luxury real estate in Provence, France"
            fill
            className="object-cover scale-105"
            priority
          />
        </div>
        <div className="absolute inset-0 bg-stone-900/50" />
        
        <div className="relative z-10 text-center space-y-8 px-6 max-w-4xl">
          <p className="font-sans text-sm uppercase tracking-[0.4em] text-stone-300 opacity-0 animate-[fadeUp_1s_ease-out_0s_forwards]">
            {hero.established || 'Est. 2008'}
          </p>
          <h1 className="font-serif text-6xl md:text-9xl text-stone-100 leading-none opacity-0 animate-[fadeUp_1s_ease-out_0.1s_forwards]">
            {hero.title || 'The Firm'}
          </h1>
          <div className="overflow-hidden">
            <p className="font-sans font-light text-lg md:text-xl text-stone-300 max-w-2xl mx-auto leading-relaxed opacity-0 animate-[fadeUp_1s_ease-out_0.2s_forwards]">
              {hero.subtitle || 'Stone Investment showcases exceptional properties, every home tells a story and embodies timeless elegance.'}
            </p>
          </div>
        </div>
        
        <div className="absolute bottom-12 animate-bounce text-stone-300" aria-hidden="true">
          <ArrowDown size={24} />
        </div>
      </header>

      {/* --- ACT 2: EDITORIAL IMAGE --- */}
      <section className="py-32 md:py-48">
        <EditorialImageSection
          {...(firm.editorialCaption ? { caption: firm.editorialCaption as string } : {})}
          {...(propertyImages ? { propertyImages } : {})}
        />
      </section>

      {/* --- ACT 3: THE PHILOSOPHY (Split Layout) --- */}
      <section className="bg-white py-32 border-y border-stone-200 overflow-hidden">
        <div className="container mx-auto px-6 md:px-24">
          <div className="mb-24 max-w-3xl">
            <h2 className="font-serif text-5xl md:text-6xl text-stone-900 mb-8">
              {(philosophy.title as string) || 'Our Extra Touch'}
            </h2>
            <p className="font-sans text-stone-600 text-lg font-light">
              {(philosophy.subtitle as string) || 'What is rare is precious. We select few properties and devote the time to them they deserve.'}
            </p>
          </div>

          <div className="flex flex-col md:flex-row gap-16">
            {/* Left: Sticky Image */}
            <div className="hidden md:block w-1/2 h-[600px] sticky top-32">
              <div className="relative w-full h-full overflow-hidden bg-stone-100 shadow-2xl">
                {philosophyItems.map((item, idx) => (
                  <Image 
                    key={idx}
                    src={item.image}
                    alt={item.title}
                    fill
                    className={`object-cover transition-all duration-1000 ease-out ${
                      activePhilosophy === idx ? 'opacity-100 scale-100' : 'opacity-0 scale-110'
                    }`}
                  />
                ))}
                {/* Overlay */}
                <div className="absolute inset-0 bg-stone-900/10" />
                <div className="absolute bottom-8 left-8 bg-white/90 backdrop-blur px-6 py-4">
                  <p className="font-serif text-2xl text-stone-900">
                    {philosophyItems[activePhilosophy]?.title ?? ((philosophy.fallbackTitle as string) || 'Our Philosophy')}
                  </p>
                </div>
              </div>
            </div>

            {/* Right: List */}
            <div className="w-full md:w-1/2 space-y-12 md:pl-12">
              {philosophyItems.map((item, idx) => (
                <div 
                  key={idx}
                  className={`group relative border-l-2 pl-8 transition-all duration-500 cursor-pointer py-4 ${
                    activePhilosophy === idx 
                      ? 'border-stone-900' 
                      : 'border-stone-300 hover:border-stone-500'
                  }`}
                  onMouseEnter={() => setActivePhilosophy(idx)}
                >
                  <span className={`text-sm uppercase tracking-[0.2em] mb-2 block font-sans font-medium transition-colors duration-500 ${
                    activePhilosophy === idx ? 'text-stone-900' : 'text-stone-600'
                  }`}>{item.subtitle}</span>
                  <h3 className={`font-serif text-3xl md:text-4xl mb-6 transition-all duration-500 ${
                    activePhilosophy === idx ? 'translate-x-2 italic text-stone-900' : 'text-stone-700'
                  }`}>
                    {item.title}
                  </h3>
                  <p className={`font-sans font-light text-stone-700 leading-relaxed text-sm transition-all duration-500 hidden md:block ${
                    activePhilosophy === idx ? 'max-h-40' : 'max-h-0 overflow-hidden'
                  }`}>
                    {item.description}
                  </p>
                  <p className="font-sans font-light text-stone-700 leading-relaxed text-sm md:hidden mt-4">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* --- ACT 4: THE TENETS (Refined Grid) --- */}
      <section className="bg-stone-900 text-stone-50 py-32 px-6 md:px-24">
        <div className="container mx-auto">
          <div className="text-center mb-20">
            <span className="text-stone-300 uppercase tracking-[0.3em] text-sm font-sans">
              {(tenets.eyebrow as string) || 'The Stone Standard'}
            </span>
            <h3 className="mt-6 font-serif text-4xl md:text-5xl text-white italic">
              {(tenets.title as string) || 'Our Tenets'}
            </h3>
          </div>

          <div ref={tenetRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-stone-800 border border-stone-800">
            {tenetItems.map((item, idx) => (
              <TenetItem
                key={idx}
                index={idx}
                icon={TENET_ICONS[idx] || Award}
                title={item.title}
                desc={item.desc}
              />
            ))}
          </div>
        </div>
      </section>

      {/* --- ACT 5: CTA --- */}
      <section className="py-40 px-6 md:px-24 bg-stone-50 flex flex-col items-center justify-center text-center relative overflow-hidden">
        {/* Decorative Background */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60vw] h-[60vw] border border-stone-200 rounded-full opacity-20 pointer-events-none animate-[spin_60s_linear_infinite]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40vw] h-[40vw] border border-stone-200 rounded-full opacity-40 pointer-events-none" />
        
        <div className="relative z-10 max-w-2xl space-y-8">
          <p className="font-sans text-sm uppercase tracking-[0.4em] text-stone-600">
            {cta.eyebrow || 'Private Office'}
          </p>
          <h2 className="font-serif text-5xl md:text-7xl text-stone-900 leading-none">
            {(() => {
              const title = cta.title || 'Begin Your Legacy';
              const legacyWord = cta.legacyWord || 'Legacy';
              const parts = title.split(legacyWord);
              if (parts.length === 2) {
                return <>{parts[0]}<span className="italic text-stone-600">{legacyWord}</span>{parts[1]}</>;
              }
              return title;
            })()}
          </h2>
          <p className="font-sans font-light text-stone-700 text-lg leading-relaxed">
            {cta.subtitle || 'Whether acquiring a masterpiece or passing one on, our partners are ready to provide the strategic guidance your assets deserve.'}
          </p>
          <div className="pt-8 flex flex-col md:flex-row gap-6 justify-center">
            <Link 
              href={`/${locale}/contact`}
              className="group relative px-12 py-5 bg-stone-900 text-white overflow-hidden shadow-2xl hover:bg-stone-800 transition-all duration-500"
            >
              <span className="relative z-10 text-xs uppercase tracking-widest group-hover:tracking-[0.2em] transition-all duration-500 flex items-center gap-4 font-sans">
                {cta.contactButton || 'Contact The Partners'} <ArrowRight size={16} />
              </span>
            </Link>
            <Link 
              href={`/${locale}/properties`}
              className="px-12 py-5 border border-stone-300 text-stone-900 uppercase tracking-widest text-xs hover:bg-stone-200 transition-colors font-sans"
            >
              {cta.collectionButton || 'View The Collection'}
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}
